<template>
  <article class="wx-article" :lang="isEnglish ? 'en' : 'zh-CN'">
    <div class="wx-inner">
      <h1 class="wx-title">{{ article.title }}</h1>
      <template v-for="(block, index) in article.blocks" :key="index">
        <div
          v-if="block.type === 'image'"
          class="wx-media"
          :class="block.align === 'center' ? 'is-center' : 'is-left'"
          :style="{ marginBottom: block.mb + 'px' }"
        >
          <img
            :src="block.src"
            :alt="article.title"
            :class="{ 'is-full': !block.width }"
            :style="block.width ? { width: block.width + 'px' } : null"
            loading="lazy"
          />
        </div>
        <p v-else-if="block.type === 'blank'" class="wx-blank" :style="blockStyle(block)"><br /></p>
        <p v-else-if="block.type === 'p'" class="wx-p" :style="blockStyle(block)">
          <span v-for="(seg, si) in block.segs" :key="si" :class="seg.c">
            <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
            <template v-else>{{ seg.t }}</template>
          </span>
        </p>
        <component
          :is="block.ordered === false ? 'ul' : 'ol'"
          v-else-if="block.type === 'list'"
          class="wx-list"
        >
          <li v-for="(item, ii) in block.items" :key="ii">
            <p class="wx-p">
              <span v-for="(seg, si) in item" :key="si" :class="seg.c">
                <a v-if="seg.u" :href="seg.u" target="_blank" rel="noopener noreferrer">{{ seg.t }}</a>
                <template v-else>{{ seg.t }}</template>
              </span>
            </p>
          </li>
        </component>
      </template>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();
const isEnglish = computed(() => locale.value === "en");

const content = {
  "zh": {
    "title": "Pruoto 全球上千家实验室广泛使用",
    "blocks": [
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/1.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "基于康码全球领先 D2P 无细胞蛋白质合成技术平台，凝聚蛋白质工厂的雄厚技术经验与积累，康码开发出专为全自动蛋白纯化打造的明星产品 Pruoto，目前已覆盖国内外上千所大学、研究所、实验室、机构和公司，获得广大师生和科研人员的普遍好评！",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/2.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "常规的实验室蛋白质纯化，长期依赖柱层析和层析仪，效率低下，重复性差，而且需要人为全程操作参与，难以满足 AI 时代越来越多，越来越快的蛋白质设计、纯化制备和快速分析的增长需求，浪费了大量研究人员的时间，严重降低了研发效率。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/3.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/4.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "因此，为满足 AI 引领的，新时代科研人员对高通量、高纯度、高一致性、蛋白纯化的需求，Pruoto™ 全自动蛋白纯化系统应运而生。这套系统由 Pruoto™ 全自动蛋白纯化仪、PrAuto® Monster® 蛋白自动纯化试剂盒，一起共同组成，设备与试剂盒一体化协同，无需人工干预即可自动完成目标蛋白质结合、清洗、洗脱的全流程。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/5.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/6.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Pruoto™ 全自动蛋白纯化仪，提供 8/16/32 通道规格，单次运行即可同时纯化 32 组目标蛋白，处理体积覆盖 30μL–4000μL，纯化蛋白产量最高可达 80mg 每次（依样品和试剂盒不同）；匹配 SBS 国际标准的 DeepWell 专用深孔板；还具有可选制冷功能，4-16℃ 自由可调，超长期保冷最长 60 天，温度实时记录，曲线一目了然，保障目标蛋白的纯化和长期存放活性。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/7.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/8.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Pruoto™ 全自动蛋白纯化仪，搭载 10 英寸彩色触摸屏，中英文显示、图形化操作界面，可实时查看实验进程与剩余时间；程序支持自定义，最多可存储 204 组；混匀与磁吸时间均可在 1–99 分钟自由调整，精准匹配不同蛋白属性；优化程序自动存储，自动调用，智能管理。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/9.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/10.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Pruoto™ 全自动蛋白纯化仪，为满足不同样本体积与实验目的设计，配套PrAuto® Monster® 蛋白自动纯化试剂盒，提供含 His-tag，GST-tag，MBP-tag， Strep-tag，Protein A\\G 等多种亲和标签，以及 Q，S，Phenol 等离子型和疏水型的各种特异性、高载量磁珠；更适配高中低样品丰度多种规格；可自动纯化抗体、binder、酶、膜蛋白、细胞因子、AI 设计蛋白、血液蛋白及带 His-tag，GST-tag，MBP-tag， Strep-tag，Fc 等各种标签的目标蛋白。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/11.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/12.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Pruoto™ 全自动蛋白纯化仪，微米磁珠高效结合目标蛋白，纯度最高可达 98%，双板纯化、多步清洗带来更均一、更高纯度的结果，约 30 分钟即可完成一次自动纯化；配合 20736 种海量蛋白纯化条件，纯化效果更均一、纯度更高、操作更方便。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/13.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/14.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "1、",
            "c": ""
          },
          {
            "t": "全自动运行，无需人工干预。",
            "c": "b"
          },
          {
            "t": "自动完成结合、清洗、洗脱全部实验流程，实验结果稳定可靠。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "2、",
            "c": ""
          },
          {
            "t": "高通量并行，单次纯化 32 组。",
            "c": "b"
          },
          {
            "t": "Pruoto™AutoPurifier-32 以 32 通道并行，单次运行即可同时纯化32组目标蛋白。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "3、",
            "c": ""
          },
          {
            "t": "高纯度结果，纯度最高可达 98%。",
            "c": "b"
          },
          {
            "t": "微米磁珠高效结合目标蛋白，双板纯化、多步清洗，结果更均一。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "4、",
            "c": ""
          },
          {
            "t": "高载量灵活选型，覆盖不同浓度样本。",
            "c": "b"
          },
          {
            "t": "Standard（8mg）、Pro（20mg）、Max（80mg）三档载量按需选择。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "5、",
            "c": ""
          },
          {
            "t": "超快速纯化。",
            "c": "b"
          },
          {
            "t": "一键启动，无需预冷，最快 30 分钟完成，效率提升 10 倍以上。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "6、",
            "c": ""
          },
          {
            "t": "低温纯化，保障蛋白高活性。",
            "c": "b"
          },
          {
            "t": "制冷模块 4-16℃ 自由可调，超长期保冷最长 60 天，温度实时记录，曲线一目了然",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "7、",
            "c": ""
          },
          {
            "t": "灵活编程、自由可调。",
            "c": "b"
          },
          {
            "t": "最多存储 204 组程序，混匀与磁吸时间 1–99 分钟自由调整。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "8、",
            "c": ""
          },
          {
            "t": "海量条件筛选，覆盖 20736 种组合。",
            "c": "b"
          },
          {
            "t": "8 种 pH buffer、4 种清洗/4 种洗脱咪唑浓度、6 种盐浓度，为条件筛选提供充分自由度。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "9、高性价比、可靠品质。",
            "c": "b"
          },
          {
            "t": "试剂盒可重复使用 6 次；设备 100% 中国制造，提供 24 小时响应、五年内硬件故障换新机的无忧售后。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/15.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/16.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "一台 Pruoto™，即可让您不再手动操作，一键点击，取代混匀仪、离心机、天平、pH 计、移液器，离心管、枪头，省去繁复人力，将蛋白纯化从低效低质手工操作，升级为全自动、高通量、高一致性、高纯度的标准化流程。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/17.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/18.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Pruoto™ 全自动蛋白纯化系统适用于各种融合标签、不同种类的重组蛋白的分离纯化，实现目标蛋白纯化条件的快速、高效筛选，适用科研场景广泛，尤其是在 AI for Science 的时代大潮下，蛋白质纯化更加进一步向，更高通量、更高效率的方向发展。Pruoto™ 为 AI 时代的高通量蛋白纯化实验提供稳定、高效、一体化的自动化解决方案。Pruoto™ 产品不仅限科研机构，更加可以满足 AI 创新公司，食品、生物制造、大健康、生物医药等多种行业的创新研发。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/19.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "如果您的实验室，仍在为蛋白纯化深陷繁复手工与低效反复，Pruoto™ 全自动蛋白纯化系统正是您期待已久的答案。",
            "c": "b"
          },
          {
            "t": "Pruoto™ 以 32 通道并行与高达 98% 的高纯度，80mg 的高产量（依样品和试剂盒不同），重新定义高通量纯化的标准；以 30 分钟的极速与 20736 种条件筛选，把科研人员的宝贵时间还给真正的科学发现；以一己之力取代多台仪器与大量人力，实现更省时、更省力、更可靠、更具性价比的一体化蛋白纯化。选择 Pruoto™，让每一次纯化，都更快、更纯、更简单。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/20.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "无细胞蛋白质是 AI 生物时代的必选之路！世界第一的全自动无细胞合成自动化智能系统在中国：康码 ProteinFactory, Pruoto, Nuoto, AMPitor, Reactor, D2Pi-AutoLab！",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "康码拥有全球领先、自主研发的 D2P 无细胞蛋白质合成技术，是全球唯一年产能万吨级量产真核无细胞表达能力的产业化技术。康码将继续以先进底层技术为基石，全力支持全社会生物智造、生物医药产业的颠覆性发展，助力科研机构与企业构建\"创新能力与研发管线\"的双螺旋发展模式、提升核心竞争力。",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/21.png",
        "width": null,
        "align": "center",
        "mb": 24
      }
    ]
  },
  "en": {
    "title": "Pruoto: Widely Used in Over a Thousand Laboratories Worldwide",
    "blocks": [
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/1.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Based on Healthcode's globally leading D2P cell-free protein synthesis technology platform, and drawing on the deep technical experience and accumulated expertise of the Protein Factory, Healthcode has developed Pruoto, a flagship product purpose-built for fully automated protein purification. It is now in use at more than a thousand universities, research institutes, laboratories, organizations and companies in China and abroad, and has won widespread praise from teachers, students and researchers.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/2.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Conventional laboratory protein purification has long depended on column chromatography and chromatography systems. It is inefficient, poorly reproducible, and requires manual involvement throughout the whole process, making it hard to meet the growing demand of the AI era for ever more and ever faster protein design, purification preparation and rapid analysis. It wastes a great deal of researchers' time and severely reduces R&D efficiency.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/3.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/4.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "To meet the needs of researchers in this new AI-led era for high-throughput, high-purity, high-consistency protein purification, the Pruoto™ fully automated protein purification system was created. The system is composed of the Pruoto™ automated protein purification instrument and the PrAuto® Monster® automated protein purification kit. Instrument and kit work together as one integrated unit, automatically completing the entire workflow of target protein binding, washing and elution without manual intervention.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/5.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/6.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "The Pruoto™ automated protein purification instrument is available in 8/16/32-channel configurations, purifying up to 32 target proteins simultaneously in a single run, with a processing volume range of 30 μL–4000 μL and a maximum protein yield of up to 80 mg per run (depending on sample and kit). It uses DeepWell plates compliant with the SBS international standard, and offers an optional refrigeration module adjustable from 4–16 °C with cold retention for up to 60 days, real-time temperature logging and clear curves, safeguarding the activity of target proteins during purification and long-term storage.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/7.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/8.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "The Pruoto™ automated protein purification instrument features a 10-inch color touchscreen with a graphical interface in Chinese and English, letting you monitor run progress and remaining time in real time. Programs can be customized and up to 204 programs can be stored; mixing and magnetic capture times can be freely adjusted from 1–99 minutes to precisely match different protein properties; optimized programs are stored and recalled automatically with intelligent management.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/9.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/10.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "The Pruoto™ automated protein purification instrument is designed for different sample volumes and experimental goals, and works with the PrAuto® Monster® automated protein purification kit. The kit provides affinity beads for His-tag, GST-tag, MBP-tag, Strep-tag, Protein A/G and more, as well as ion-exchange and hydrophobic magnetic beads such as Q, S and Phenol types, with various specific, high-capacity options. It also supports multiple specifications for high, medium and low sample abundance, and can automatically purify antibodies, binders, enzymes, membrane proteins, cytokines, AI-designed proteins, blood proteins and target proteins carrying His-tag, GST-tag, MBP-tag, Strep-tag, Fc and other tags.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/11.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/12.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "The Pruoto™ automated protein purification instrument uses micron-sized magnetic beads to bind target proteins with high efficiency, achieving purity of up to 98%. Dual-plate purification and multi-step washing deliver more uniform, higher-purity results, and a single automated purification run takes about 30 minutes. Combined with 20,736 protein purification condition combinations, it makes purification more uniform, higher in purity and easier to operate.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/13.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/14.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "1. ",
            "c": ""
          },
          {
            "t": "Fully automated operation with no manual intervention.",
            "c": "b"
          },
          {
            "t": " Binding, washing and elution are all completed automatically, giving stable and reliable results.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "2. ",
            "c": ""
          },
          {
            "t": "High-throughput parallel processing, up to 32 purifications per run.",
            "c": "b"
          },
          {
            "t": " Pruoto™ AutoPurifier-32 runs 32 channels in parallel, purifying 32 target proteins simultaneously in a single run.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "3. ",
            "c": ""
          },
          {
            "t": "High-purity results, up to 98% purity.",
            "c": "b"
          },
          {
            "t": " Micron-sized magnetic beads bind target proteins efficiently, with dual-plate purification and multi-step washing for more uniform results.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "4. ",
            "c": ""
          },
          {
            "t": "Flexible high-capacity options for samples of different concentrations.",
            "c": "b"
          },
          {
            "t": " Choose from three capacity tiers as needed: Standard (8 mg), Pro (20 mg) and Max (80 mg).",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "5. ",
            "c": ""
          },
          {
            "t": "Ultra-fast purification.",
            "c": "b"
          },
          {
            "t": " One-click start with no pre-cooling, completing in as little as 30 minutes — more than 10× the efficiency.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "6. ",
            "c": ""
          },
          {
            "t": "Low-temperature purification that protects protein activity.",
            "c": "b"
          },
          {
            "t": " The refrigeration module is freely adjustable from 4–16 °C with cold retention for up to 60 days, plus real-time temperature logging and clear curves.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "7. ",
            "c": ""
          },
          {
            "t": "Flexible programming, freely adjustable.",
            "c": "b"
          },
          {
            "t": " Store up to 204 programs; mixing and magnetic capture times are freely adjustable from 1–99 minutes.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "8. ",
            "c": ""
          },
          {
            "t": "Massive condition screening across 20,736 combinations.",
            "c": "b"
          },
          {
            "t": " 8 pH buffers, 4 wash / 4 elution imidazole concentrations and 6 salt concentrations provide ample freedom for condition screening.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "9. High cost-performance and reliable quality.",
            "c": "b"
          },
          {
            "t": " The kit can be reused up to 6 times; the instrument is 100% made in China, backed by 24-hour response and worry-free after-sales service with hardware replacement within five years.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/15.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/16.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "A single Pruoto™ frees you from manual operation: with one click it replaces the mixer, centrifuge, balance, pH meter, pipettes, centrifuge tubes and pipette tips, removing laborious manual work and upgrading protein purification from inefficient, low-quality manual operation to a fully automated, high-throughput, high-consistency, high-purity standardized workflow.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/17.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/18.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "The Pruoto™ fully automated protein purification system is suitable for the separation and purification of recombinant proteins with various fusion tags and of many different types, enabling fast and efficient screening of purification conditions for target proteins. It serves a wide range of research scenarios, especially amid the AI for Science wave, where protein purification is moving further toward higher throughput and higher efficiency. Pruoto™ provides a stable, efficient and integrated automation solution for high-throughput protein purification in the AI era. Pruoto™ products are not limited to research institutions; they also meet the innovative R&D needs of AI companies and industries such as food, biomanufacturing, health and biomedicine.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/19.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "If your lab is still struggling with laborious manual work and inefficient repetition in protein purification, the Pruoto™ fully automated protein purification system is the long-awaited answer.",
            "c": "b"
          },
          {
            "t": " With 32-channel parallel processing, purity of up to 98% and a high yield of 80 mg (depending on sample and kit), Pruoto™ redefines the standard for high-throughput purification. With 30-minute speed and 20,736 condition combinations, it gives researchers' precious time back to real scientific discovery. On its own it replaces multiple instruments and a great deal of manpower, delivering more time-saving, effort-saving, reliable and cost-effective integrated protein purification. Choose Pruoto™ and make every purification faster, purer and simpler.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/20.png",
        "width": null,
        "align": "center",
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Cell-free protein is the inevitable path of the AI biotech era! The world's first fully automated cell-free synthesis intelligent system is in China: Healthcode ProteinFactory, Pruoto, Nuoto, AMPitor, Reactor and D2Pi-AutoLab!",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "p",
        "segs": [
          {
            "t": "Healthcode owns the globally leading, independently developed D2P cell-free protein synthesis technology — the world's only industrialized technology capable of producing eukaryotic cell-free expression at a ten-thousand-ton annual scale. Healthcode will continue to build on its advanced underlying technology to fully support the disruptive development of biomanufacturing and biomedicine across society, helping research institutions and companies build a double-helix model of \"innovation capability and R&D pipeline\" and strengthen their core competitiveness.",
            "c": ""
          }
        ],
        "mt": 24,
        "mb": 24
      },
      {
        "type": "image",
        "src": "https://www.healthcode.com/media/pruoto1026/21.png",
        "width": null,
        "align": "center",
        "mb": 24
      }
    ]
  }
};

// 还原原文里显式写出的上下间距（空行块、部分段落）
const blockStyle = (block) => ({
  marginTop: block.mt !== undefined ? block.mt + "px" : null,
  marginBottom: block.mb !== undefined ? block.mb + "px" : null,
});

const article = computed(() => (isEnglish.value ? content.en : content.zh));
</script>

<style scoped>
.wx-article {
  width: 100%;
  min-height: 100vh;
  padding: 8rem 0 4rem;
  background: #fff;
}

.wx-inner {
  max-width: 677px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  font-size: 17px;
  line-height: 1.75;
  letter-spacing: 0.034em;
  color: #333;
  text-align: justify;
  overflow-wrap: break-word;
}

.wx-title {
  margin: 0 0 24px;
  color: #222;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.4;
  text-align: left;
}

.wx-p {
  margin: 0 0 16px;
}

/* 原文用 <br> / &nbsp; 撑出的空行，保留一个行高的留白 */
.wx-blank {
  margin: 0;
  line-height: 1.75;
}

.wx-media {
  margin: 0 0 16px;
}

.wx-media.is-center {
  text-align: center;
}

.wx-media.is-left {
  text-align: left;
}

.wx-media img {
  display: inline-block;
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

.wx-media img.is-full {
  width: 100%;
}

.wx-list {
  margin: 0 0 16px;
  padding-left: 1.75em;
  list-style-type: decimal;
}

.wx-list li > .wx-p {
  margin-bottom: 8px;
}

.wx-list li:last-child > .wx-p {
  margin-bottom: 0;
}

.hl {
  color: rgb(0, 128, 255);
  font-weight: bold;
}

.wx-inner a {
  color: inherit;
  text-decoration: inherit;
}

.b {
  font-weight: bold;
}

.u {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .wx-article {
    padding-top: 7rem;
  }

  .wx-inner {
    font-size: 16px;
  }

  .wx-title {
    font-size: 19px;
  }
}
</style>
